import React, { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, Loader2 } from "lucide-react";
import ImageCropModal from "../../../../shared/ImageCropModal/ImageCropModal";
import "./styles/CollectionBannerUploader.css";

/** Wide banner, matching how it's displayed on the live collection page. */
const BANNER_ASPECT_RATIO = 2.2;
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_SIZE_MB = 5;

interface CollectionBannerUploaderProps {
  /** Existing banner URL (edit mode) — shown when no new file is staged. */
  existingBannerUrl: string | null;
  /** Fires with the cropped file once the user confirms the crop. */
  onFileSelected: (file: File) => void;
  onInvalidFile?: (reason: string) => void;
  isUploading?: boolean;
}

/**
 * Banner upload + crop for Create/Edit Collection. Picking a file opens the
 * crop modal immediately (fixed aspect ratio); only the cropped result is
 * staged, mirroring RepresentativeImagesProgram's crop flow.
 */
const CollectionBannerUploader: React.FC<CollectionBannerUploaderProps> = ({
  existingBannerUrl,
  onFileSelected,
  onInvalidFile,
  isUploading = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    return () => {
      if (cropSrc) URL.revokeObjectURL(cropSrc);
    };
  }, [cropSrc]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      onInvalidFile?.("Please select a JPEG, PNG, or WEBP image.");
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      onInvalidFile?.(`Image must be smaller than ${MAX_SIZE_MB}MB.`);
      return;
    }
    setCropSrc(URL.createObjectURL(file));
  };

  const handleCropCancel = () => setCropSrc(null);

  const handleCropComplete = (croppedBlob: Blob) => {
    setCropSrc(null);
    const croppedFile = new File([croppedBlob], "collection-banner.jpg", {
      type: "image/jpeg",
    });
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(croppedBlob));
    onFileSelected(croppedFile);
  };

  const displayedSrc = preview ?? existingBannerUrl;

  return (
    <div className="coll-banner-uploader">
      {cropSrc && (
        <ImageCropModal
          imageSrc={cropSrc}
          aspectRatio={BANNER_ASPECT_RATIO}
          onCancel={handleCropCancel}
          onCropComplete={handleCropComplete}
        />
      )}

      <button
        type="button"
        className={`coll-banner-uploader-btn ${displayedSrc ? "coll-banner-uploader-btn--filled" : ""} ${isUploading ? "is-uploading" : ""}`}
        onClick={() => !isUploading && inputRef.current?.click()}
        disabled={isUploading}
      >
        {displayedSrc ? (
          <img src={displayedSrc} alt="Collection banner" className="coll-banner-uploader-preview" />
        ) : (
          <div className="coll-banner-uploader-placeholder">
            <ImagePlus size={26} color="#94a3b8" />
            <span>Add Banner Image</span>
          </div>
        )}

        <div className="coll-banner-uploader-badge">
          {isUploading ? (
            <Loader2 size={14} color="#fff" className="coll-banner-uploader-spinner" />
          ) : (
            <Camera size={14} color="#fff" />
          )}
        </div>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="coll-banner-uploader-input"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default CollectionBannerUploader;
